// src/pages/Reports.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardHeader, CardContent, IconButton, TextField, MenuItem,
  Button, Tooltip, Grid, Stack, Typography, Divider, Table, TableBody,
  TableCell, TableHead, TableRow, TableContainer, Chip, Paper
} from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import { loadAllRawRecords } from '../utils/dataLoader';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { useI18n } from '../i18n/i18n';

const fmt = (n, d = 2) => (Number.isFinite(n) ? n.toFixed(d) : '—');

const shifts = [
  { label: '—', value: '' },
  { label: 'mañana', value: 'mañana' },
  { label: 'tarde', value: 'tarde' },
];

const Stat = ({ label, value }) => (
  <Box sx={{ textAlign: 'center', p: 2 }}>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{label}</Typography>
    <Typography variant="h4" fontWeight="bold" color="primary.main">{value}</Typography>
  </Box>
);

export default function Reports() {
  const { t } = useI18n();
  const [raw, setRaw] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [shift, setShift] = useState('');

  const reload = useCallback(async () => {
    setLoading(true);
    const rows = await loadAllRawRecords();
    setRaw(Array.isArray(rows) ? rows : []);
    setLoading(false);
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const records = useMemo(() => {
    return (raw || []).filter(r => r.recordType === 'shift_report')
      .sort((a, b) => new Date(a.fecha) - new Date(b.fecha));
  }, [raw]);

  const filtered = useMemo(() => {
    const fromTs = dateFrom ? new Date(`${dateFrom}T00:00:00`).getTime() : null;
    const toTs = dateTo ? new Date(`${dateTo}T23:59:59`).getTime() : null;
    return records.filter(r => {
      const tms = new Date(r.fecha).getTime();
      if (fromTs && tms < fromTs) return false;
      if (toTs && tms > toTs) return false;
      if (shift && r.turno !== shift) return false;
      return true;
    });
  }, [records, dateFrom, dateTo, shift]);

  const summary = useMemo(() => {
    const data = { kg: 0, rejectKg: 0, clientWeight: 0, cycles: 0, count: 0 };
    filtered.forEach(r => {
      data.count++;
      data.kg += Object.values(r.tunel || {}).reduce((s, v) => s + Number(v || 0), 0);
      data.rejectKg += Object.values(r.rech_tunel || {}).reduce((s, v) => s + Number(v || 0), 0);
      data.cycles += Object.values(r.lav_ciclos || {}).reduce((s, v) => s + Number(v || 0), 0);
      data.clientWeight += (r.pesoCliente || []).reduce((s, v) => s + Number(v.weight || 0), 0);
    });
    return data;
  }, [filtered]);

  const byTunnelProgram = useMemo(() => {
    const agg = {};
    filtered.forEach(r => {
      Object.entries(r.tunel || {}).forEach(([prog, kg]) => {
        agg[prog] = (agg[prog] || 0) + Number(kg || 0);
      });
    });
    return Object.entries(agg).map(([name, kg]) => ({ name, kg: parseFloat(kg.toFixed(2)) })).sort((a, b) => b.kg - a.kg);
  }, [filtered]);

  const byClientWeight = useMemo(() => {
    const agg = {};
    filtered.forEach(r => {
      (r.pesoCliente || []).forEach(item => {
        if (item.client && item.weight) {
          agg[item.client] = (agg[item.client] || 0) + Number(item.weight || 0);
        }
      });
    });
    return Object.entries(agg).map(([name, kg]) => ({ name, kg: parseFloat(kg.toFixed(2)) })).sort((a, b) => b.kg - a.kg);
  }, [filtered]);

  const byMeterReadings = useMemo(() => {
    if (filtered.length < 2) return {};
    const first = filtered[0].cont || {};
    const last = filtered[filtered.length - 1].cont || {};
    const consumption = {};
    for (const key in last) {
      if (key in first) {
        const diff = Number(last[key] || 0) - Number(first[key] || 0);
        if (diff > 0) {
          consumption[key] = diff;
        }
      }
    }
    return consumption;
  }, [filtered]);

  const exportToPDF = () => {
    // Implementation for PDF export
    console.log('Exporting to PDF...');
  };

  const exportToExcel = () => {
    // Implementation for Excel export
    console.log('Exporting to Excel...');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardHeader
          title={
            <Typography variant="h4" fontWeight="bold">
              {t('reports_detailed_title')}
            </Typography>
          }
          subheader={
            <Typography variant="subtitle1" color="text.secondary">
              {t('reports_detailed_subtitle')}
            </Typography>
          }
          action={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title={t('actions_exportPdf')}>
                <IconButton onClick={exportToPDF} color="primary">
                  <PictureAsPdfIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('actions_exportXlsx')}>
                <IconButton onClick={exportToExcel} color="primary">
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('common_refresh')}>
                <IconButton onClick={reload} color="primary">
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
          }
        />
        
        <CardContent>
          {/* Filters Section */}
          <Paper sx={{ p: 2, mb: 3, borderRadius: 2, backgroundColor: 'background.default' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>{t('filters')}</Typography>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap">
              <TextField 
                label={t('filters_from')} 
                type="date" 
                size="small" 
                value={dateFrom} 
                onChange={(e) => setDateFrom(e.target.value)} 
                InputLabelProps={{ shrink: true }} 
                sx={{ width: 160 }} 
              />
              <TextField 
                label={t('filters_to')} 
                type="date" 
                size="small" 
                value={dateTo} 
                onChange={(e) => setDateTo(e.target.value)} 
                InputLabelProps={{ shrink: true }} 
                sx={{ width: 160 }} 
              />
              <TextField 
                select 
                label={t('labels_shift')} 
                size="small" 
                value={shift} 
                onChange={(e) => setShift(e.target.value)} 
                sx={{ width: 140 }}
              >
                {shifts.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
              </TextField>
              <Button 
                variant="outlined" 
                onClick={() => { setDateFrom(''); setDateTo(''); setShift(''); }}
                sx={{ height: 40 }}
              >
                {t('actions_clear')}
              </Button>
            </Stack>

            {/* Summary Cards */}
            <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {t('reports_period')}: {dateFrom || '—'} — {dateTo || '—'}
                  </Typography>
                  <Chip 
                    label={`${t('reports_records')}: ${summary.count}`} 
                    color="primary" 
                    variant="outlined" 
                  />
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Stat label={t('reports_total_production')} value={fmt(summary.kg)} />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Stat label={t('reports_total_rejection')} value={fmt(summary.rejectKg)} />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Stat label={t('reports_total_cycles')} value={fmt(summary.cycles)} />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Stat label={t('reports_total_client_weight')} value={fmt(summary.clientWeight)} />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Paper>

          {/* Meter Readings Table */}
          <Card sx={{ mb: 3, borderRadius: 2 }}>
            <CardHeader 
              title={
                <Typography variant="h5" fontWeight="bold">
                  {t('reports_meter_summary')}
                </Typography>
              }
            />
            <CardContent>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.default' }}>
                        {t('reports_meter_header_name')}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: 'background.default' }}>
                        {t('reports_meter_header_consumption')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.keys(byMeterReadings).length > 0 ? (
                      Object.entries(byMeterReadings).map(([key, value]) => (
                        <TableRow key={key} hover>
                          <TableCell>{key}</TableCell>
                          <TableCell align="right">{fmt(value)}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={2} align="center">
                          <Typography color="text.secondary" sx={{ py: 2 }}>
                            {t('archive_empty')}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Charts Section */}
          <Grid container spacing={3}>
            <Grid item xs={12} lg={6}>
              <Card sx={{ height: 400, borderRadius: 2 }}>
                <CardHeader 
                  title={
                    <Typography variant="h6" fontWeight="bold">
                      {t('reports_by_tunnel_program')}
                    </Typography>
                  }
                />
                <CardContent sx={{ height: '85%' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={byTunnelProgram} layout="vertical" margin={{ top: 5, right: 30, left: 30, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 12 }} />
                      <RechartsTooltip formatter={(value) => `${fmt(value)} ${t('labels_kg')}`} />
                      <Bar dataKey="kg" fill="#8884d8" name={t('labels_kg')} radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} lg={6}>
              <Card sx={{ height: 400, borderRadius: 2 }}>
                <CardHeader 
                  title={
                    <Typography variant="h6" fontWeight="bold">
                      {t('reports_by_client_weight')}
                    </Typography>
                  }
                />
                <CardContent sx={{ height: '85%' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={byClientWeight.slice(0, 10)} layout="vertical" margin={{ top: 5, right: 30, left: 30, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 12 }} />
                      <RechartsTooltip formatter={(value) => `${fmt(value)} ${t('labels_kg')}`} />
                      <Legend />
                      <Bar dataKey="kg" fill="#82ca9d" name={t('reports_client_weight_legend')} radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
}


